import React, { useState, useEffect, useCallback } from 'react';
import { auditLogAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import DateRangeCard from '../../components/DateRangeCard/DateRangeCard';
import DataTable from '../../components/DataTable/DataTable';
import { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import { exportRowsToExcel } from '../../utils/excel';

// The most the server gives at once (backend auditLog.controller getAll).
const EXPORT_MAX = 2000;
const EXPORT_COLUMNS = [
    { label: 'Date Time', field: 'createdAt' },
    { label: 'Username', field: 'username' },
    { label: 'Action', field: 'action' },
    { label: 'Details', field: 'details' },
    { label: 'IP Address', field: 'ipAddress' },
    { label: 'URL', field: 'url' },
];

const AdminActivity = () => {
    const { selectedBranch } = useBranchStore();
    const [range, setRange] = useState({ start: '', end: '' });
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    // The date and time picked, as the server takes them (ISO — it has no idea of India time).
    const queryParams = useCallback(() => {
        const { from, to } = rangeParams({ from: range.start, to: range.end });
        const params = { branchId: selectedBranch?._id };
        if (from) params.from = from;
        if (to) params.to = to;
        return params;
    }, [selectedBranch, range]);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const res = await auditLogAPI.getAll(queryParams());
            setLogs(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch activity log');
        } finally {
            setLoading(false);
        }
    }, [queryParams]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    // The range's activity — as much as the server gives at once — as an Excel file.
    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await auditLogAPI.getAll({ ...queryParams(), limit: EXPORT_MAX });
            const rows = res.data.data || [];
            exportRowsToExcel(`login-activity-${new Date().toISOString().slice(0, 10)}`, EXPORT_COLUMNS, rows, { sheetName: 'Login & Activity' });
            if (rows.length >= EXPORT_MAX) toast.info(`The latest ${EXPORT_MAX} entries were exported — pick a shorter date range for the rest.`);
        } catch (error) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-history"></i>
                            Login &amp; Activity
                        </h1>
                        <p className="page-subtitle-modern">Every admin action recorded across the platform, with who did it and from where</p>
                    </div>
                </div>
            </div>

            <DateRangeCard withTime value={range} onApply={setRange} onClear={() => setRange({ start: '', end: '' })} />

            <div className="ad-card" style={{ marginTop: '1.5rem' }}>
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-history"></i>
                        <h2>Dashboard Login &amp; Activity</h2>
                    </div>
                    <button type="button" className="btn-secondary-modern btn-sm" onClick={handleExport} disabled={exporting || loading || logs.length === 0}>
                        <i className="fas fa-file-excel"></i> {exporting ? 'Exporting…' : 'Excel'}
                    </button>
                </div>
                <div style={{ padding: '1.125rem 1.375rem' }}>
                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div><p>Loading activity log...</p></div>
                    ) : (
                        <DataTable
                            defaultPageSize={100}
                            columns={[
                                { label: 'Sr. No.', field: 'sr' },
                                { label: 'Date Time', field: 'dateTime' },
                                { label: 'Username', field: 'username' },
                                { label: 'Action', field: 'action' },
                                { label: 'Details', field: 'details' },
                                { label: 'IP Address', field: 'ipAddress' },
                                { label: 'URL', field: 'url' },
                            ]}
                            rows={logs.map((log, i) => ({
                                id: log._id,
                                sr: i + 1,
                                dateTime: log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN') : '—',
                                username: log.username,
                                action: log.action,
                                details: log.details,
                                ipAddress: log.ipAddress || '—',
                                url: log.url || '—',
                            }))}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminActivity;
